import {Menu, Stack, Text, Tooltip, UnstyledButton, createStyles, getStylesRef} from '@mantine/core';
import React from 'react';

import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import ComponentRow from './ComponentRow';
import type {EntitySearchTab} from './entity-search/EntitySearchModal';
import {
  type ComponentKind,
  type ComposedCapabilities,
  getComponentLabel,
  type PredictionComponent,
  makeComponent,
} from './newPredictionState';

interface InputComponentsEditorProps {
  capabilities: ComposedCapabilities;
  components: Array<PredictionComponent>;
  disabled?: boolean;
  onComponentsChange: (components: Array<PredictionComponent>) => void;
  // Offered alongside typing a value when the host can search the registry.
  onSearchEntities?: (tab: EntitySearchTab) => void;
}

/** The per-input editor: a grid of component cards plus a ghost "Add component" card for multimers. */
export default function InputComponentsEditor({
  capabilities,
  components,
  disabled = false,
  onComponentsChange,
  onSearchEntities,
}: InputComponentsEditorProps): JSX.Element {
  const {classes} = useStyles();
  const modelSupportsMultimers = capabilities.supportsMultimers;
  const moleculesSupported = capabilities.supportsMolecules;
  const maxComponents = capabilities.maxComponents;
  const isAtComponentLimit = components.length >= maxComponents;
  const canAddComponent = !disabled && modelSupportsMultimers && !isAtComponentLimit;
  // The limit is the most restrictive across every selected model, so word it against the selection
  // rather than a single model.
  const addComponentTooltip =
    isAtComponentLimit && !disabled ? `Maximum ${maxComponents} components for the selected models` : null;

  const handleAddComponent = (): void => {
    onComponentsChange([...components, makeComponent('aminoAcids')]);
  };

  // Without an onClick it's a menu target, and Menu wires up the click.
  const addComponentCard = (onClick?: () => void): JSX.Element => (
    <UnstyledButton
      aria-label="Add component"
      className={classes.addComponentCard}
      disabled={!canAddComponent}
      onClick={onClick}
      type="button"
    >
      <Stack align="center" justify="center" spacing="xs">
        <BenchlingIcon className={classes.addComponentIcon} icon="plus" />
        <Text className={classes.addComponentLabel} size="sm">
          Add component
        </Text>
      </Stack>
    </UnstyledButton>
  );

  return (
    <div className={classes.componentGrid}>
      {components.map((component, index) => (
        <ComponentRow
          key={component.id}
          component={component}
          copiesDisabled={!modelSupportsMultimers}
          disabled={disabled}
          isOnlyComponent={components.length === 1}
          isTypeDisabled={disabled || !modelSupportsMultimers}
          label={getComponentLabel(components, index)}
          maxCopies={maxComponents}
          moleculesSupported={moleculesSupported}
          onCopiesChange={(copies) => {
            onComponentsChange(
              components.map((item) => (item.id === component.id ? {...item, copies} : item))
            );
          }}
          onRemove={() => {
            onComponentsChange(components.filter((item) => item.id !== component.id));
          }}
          onTypeChange={(type: ComponentKind) => {
            onComponentsChange(
              components.map((item) =>
                item.id === component.id ? {...item, type, value: '', status: {kind: 'empty'}} : item
              )
            );
          }}
          onValueChange={(value) => {
            onComponentsChange(
              components.map((item) =>
                item.id === component.id ? {...item, value, status: {kind: 'empty'}} : item
              )
            );
          }}
        />
      ))}
      {modelSupportsMultimers && (
        <Tooltip disabled={addComponentTooltip == null} label={addComponentTooltip ?? undefined}>
          <span className={classes.addComponentWrapper}>
            {onSearchEntities == null ? (
              addComponentCard(handleAddComponent)
            ) : (
              <Menu position="bottom" withinPortal={true}>
                <Menu.Target>{addComponentCard()}</Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item icon={<BenchlingIcon icon="pen" />} onClick={handleAddComponent}>
                    Paste data
                  </Menu.Item>
                  <Menu.Item
                    icon={<BenchlingIcon icon="search" />}
                    onClick={() => {
                      onSearchEntities('entities');
                    }}
                  >
                    Search for entities
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
          </span>
        </Tooltip>
      )}
    </div>
  );
}

const useStyles = createStyles((theme) => ({
  componentGrid: {
    display: 'grid',
    gap: theme.spacing.md,
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
  },
  addComponentWrapper: {
    display: 'block',
    height: '100%',
    minWidth: 0,
  },
  addComponentCard: {
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.5,
    },
    '&:focus-visible:not(:disabled), &:hover:not(:disabled)': {
      backgroundColor: theme.colors.gray[0],
      borderColor: theme.colors.gray[6],
      [`& .${getStylesRef('addComponentIcon')}`]: {
        color: theme.colors.gray[7],
      },
      [`& .${getStylesRef('addComponentLabel')}`]: {
        color: theme.colors.gray[8],
      },
    },
    alignItems: 'center',
    border: `2px dashed ${theme.colors.gray[4]}`,
    borderRadius: theme.radius.md,
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    minHeight: 140,
    padding: theme.spacing.lg,
    transition: 'background-color 150ms ease, border-color 150ms ease, color 150ms ease',
    width: '100%',
  },
  addComponentIcon: {
    ref: getStylesRef('addComponentIcon'),
    color: theme.colors.gray[6],
    fontSize: 20,
    transition: 'color 150ms ease',
  },
  addComponentLabel: {
    ref: getStylesRef('addComponentLabel'),
    color: theme.colors.gray[7],
    fontWeight: 600,
    transition: 'color 150ms ease',
  },
}));
